import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Plus, Pencil, Trash2, Sparkles, Save, CheckCircle2,
  X, Search, Wrench, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const PART_FIELDS = [
  { key: "pump_1", label: "Pompe massage 1" },
  { key: "pump_2", label: "Pompe massage 2" },
  { key: "circulation_pump", label: "Pompe de circulation" },
  { key: "heater", label: "Réchauffeur", withKw: true },
  { key: "keypad", label: "Clavier" },
  { key: "controller", label: "Carte électronique" },
  { key: "filter_cartridge", label: "Filtre cartouche" },
  { key: "blower", label: "Blower" },
  { key: "ozonator", label: "Ozonateur" },
];

const EMPTY_MODEL = {
  brand: "",
  model_name: "",
  year_range: "",
  seats: null,
  dimensions: "",
  pump_1: null,
  pump_2: null,
  circulation_pump: null,
  heater: null,
  keypad: null,
  controller: null,
  filter_cartridge: null,
  blower: null,
  ozonator: null,
  extras: [],
  notes: "",
  verified: true,
};

export default function AdminSpaParts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/spa-parts/models");
      setItems(r.data.models || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (m) => m.brand.toLowerCase().includes(q) || m.model_name.toLowerCase().includes(q)
    );
  }, [items, search]);

  const openNew = () => {
    setEditing({ ...EMPTY_MODEL });
    setAiSuggestion(null);
  };

  const openEdit = (m) => {
    setEditing({
      ...EMPTY_MODEL,
      ...m,
      extras: m.extras || [],
    });
    setAiSuggestion(null);
    setTimeout(() => document.getElementById("editor")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const submit = async () => {
    if (!editing?.brand || !editing?.model_name) {
      toast.error("Marque et modèle obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        seats: editing.seats ? Number(editing.seats) : null,
      };
      if (editing.id) {
        await api.put(`/admin/spa-parts/models/${editing.id}`, payload);
        toast.success("Modèle mis à jour");
      } else {
        await api.post("/admin/spa-parts/models", payload);
        toast.success("Modèle créé");
      }
      setEditing(null);
      setAiSuggestion(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/spa-parts/models/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const triggerAi = async () => {
    if (!editing?.brand || !editing?.model_name) {
      toast.error("Renseignez d'abord la marque et le modèle");
      return;
    }
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const r = await api.post("/spa-parts/ai-suggest", {
        brand: editing.brand,
        model_name: editing.model_name,
      });
      setAiSuggestion(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    setEditing((prev) => ({
      ...prev,
      ...Object.fromEntries(
        PART_FIELDS.map((f) => [f.key, aiSuggestion.parts?.[f.key] || null])
      ),
      verified: false, // Force à Non vérifié par défaut
      notes: (prev.notes ? prev.notes + "\n\n" : "") +
        `[Suggestion IA · confiance ${aiSuggestion.confidence}] ${aiSuggestion.reasoning || ""}`.trim(),
    }));
    toast.success("Suggestion IA appliquée — à valider avant enregistrement");
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="admin-spa-parts-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Admin · Pièces détachées</div>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Catalogue modèles ↔ pièces</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Gérez les modèles de spas et leurs pièces standards. Utilisez l'IA Claude pour pré-remplir les
            modèles absents, validez puis enregistrez.
          </p>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="admin-parts-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau modèle
        </Button>
      </div>

      {/* Editor */}
      {editing && (
        <Card id="editor" className="mt-8 p-6 border-black/10" data-testid="admin-parts-editor">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-display text-lg font-bold">
              {editing.id ? `Éditer : ${editing.brand} — ${editing.model_name}` : "Nouveau modèle"}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setEditing(null); setAiSuggestion(null); }}
              data-testid="admin-parts-close-editor"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Meta */}
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Field label="Marque" required>
              <Input
                value={editing.brand}
                onChange={(e) => setEditing({ ...editing, brand: e.target.value })}
                placeholder="Ex : Jacuzzi"
                data-testid="admin-parts-brand"
              />
            </Field>
            <Field label="Modèle" required>
              <Input
                value={editing.model_name}
                onChange={(e) => setEditing({ ...editing, model_name: e.target.value })}
                placeholder="Ex : J-345"
                data-testid="admin-parts-model"
              />
            </Field>
            <Field label="Années">
              <Input
                value={editing.year_range || ""}
                onChange={(e) => setEditing({ ...editing, year_range: e.target.value })}
                placeholder="2019-2024"
                data-testid="admin-parts-year"
              />
            </Field>
            <Field label="Places">
              <Input
                type="number"
                min={1}
                max={20}
                value={editing.seats ?? ""}
                onChange={(e) => setEditing({ ...editing, seats: e.target.value ? Number(e.target.value) : null })}
                data-testid="admin-parts-seats"
              />
            </Field>
            <Field label="Dimensions" full>
              <Input
                value={editing.dimensions || ""}
                onChange={(e) => setEditing({ ...editing, dimensions: e.target.value })}
                placeholder="213 x 213 x 91 cm"
                data-testid="admin-parts-dimensions"
              />
            </Field>
          </div>

          {/* AI trigger */}
          <div className="mt-6 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4" data-testid="admin-parts-ai-block">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold text-primary">
                  <Sparkles className="h-4 w-4" /> Assistance IA
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pré-remplir automatiquement les pièces en interrogeant Claude Sonnet 4.5.
                </p>
              </div>
              <Button
                type="button"
                onClick={triggerAi}
                disabled={aiLoading || !editing.brand || !editing.model_name}
                className="rounded-md"
                data-testid="admin-parts-ai-trigger"
              >
                {aiLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {aiLoading ? "Analyse IA…" : "Suggérer les pièces"}
              </Button>
            </div>

            {aiSuggestion && (
              <div className="mt-4 border-t border-primary/10 pt-4" data-testid="admin-parts-ai-result">
                <div className="text-xs mb-2 flex flex-wrap gap-2">
                  <span className="px-2 py-1 rounded-full bg-white font-semibold">Confiance : {aiSuggestion.confidence}</span>
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">Non vérifié</span>
                </div>
                {aiSuggestion.reasoning && (
                  <p className="text-xs text-muted-foreground italic">{aiSuggestion.reasoning}</p>
                )}
                <Button
                  size="sm"
                  onClick={applyAiSuggestion}
                  className="mt-3 rounded-md bg-emerald-600 hover:bg-emerald-700"
                  data-testid="admin-parts-ai-apply"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Appliquer aux champs pièces
                </Button>
              </div>
            )}
          </div>

          {/* Parts editors */}
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {PART_FIELDS.map((pf) => (
              <PartEditor
                key={pf.key}
                label={pf.label}
                withKw={pf.withKw}
                value={editing[pf.key]}
                onChange={(v) => setEditing({ ...editing, [pf.key]: v })}
                testid={`admin-parts-${pf.key}`}
              />
            ))}
          </div>

          <Field label="Notes" full hint="Ajoutez toute information utile (compatibilités, spécificités millésime…)">
            <Textarea
              rows={3}
              value={editing.notes || ""}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              data-testid="admin-parts-notes"
            />
          </Field>

          <div className="mt-4 flex items-center gap-3">
            <Switch
              checked={editing.verified}
              onCheckedChange={(v) => setEditing({ ...editing, verified: v })}
              data-testid="admin-parts-verified"
            />
            <span className="text-sm">
              {editing.verified ? "Vérifié par un technicien" : "Non vérifié (visible mais marqué draft)"}
            </span>
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              onClick={submit}
              disabled={saving}
              className="rounded-full"
              data-testid="admin-parts-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editing.id ? "Enregistrer" : "Créer"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setEditing(null); setAiSuggestion(null); }}
              data-testid="admin-parts-cancel-btn"
            >
              Annuler
            </Button>
          </div>
        </Card>
      )}

      {/* Search + list */}
      <div className="mt-10">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrer par marque ou modèle…"
            className="pl-10"
            data-testid="admin-parts-search"
          />
        </div>
      </div>

      <Card className="mt-4 border-black/10 overflow-hidden" data-testid="admin-parts-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Aucun modèle.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Marque · Modèle</th>
                <th className="text-left p-3">Années · Places</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-black/5" data-testid={`admin-parts-row-${m.id}`}>
                  <td className="p-3">
                    <div className="font-semibold">{m.brand}</div>
                    <div className="text-xs text-muted-foreground">{m.model_name}</div>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {m.year_range} {m.seats && `· ${m.seats} places`}
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      m.source === "ai" ? "bg-purple-100 text-purple-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {m.source === "ai" ? "IA" : "manuel"}
                    </span>
                  </td>
                  <td className="p-3">
                    {m.verified ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                        <CheckCircle2 className="h-3 w-3" /> Vérifié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">
                        <AlertCircle className="h-3 w-3" /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => openEdit(m)}
                      className="inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-black/5"
                      data-testid={`admin-parts-edit-${m.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => remove(m.id, `${m.brand} ${m.model_name}`)}
                      className="inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-black/5 text-destructive"
                      data-testid={`admin-parts-delete-${m.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function PartEditor({ label, value, onChange, withKw, testid }) {
  const v = value || {};
  const setField = (k, val) => {
    const next = { ...v, [k]: val };
    // Si tout est vide, remonter null
    const hasAny = Object.values(next).some((x) => x && String(x).trim());
    onChange(hasAny ? next : null);
  };
  return (
    <div className="rounded-xl border border-black/10 p-3" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Input
          placeholder="Marque"
          value={v.brand || ""}
          onChange={(e) => setField("brand", e.target.value)}
          className="h-9 text-sm"
          data-testid={`${testid}-brand`}
        />
        <Input
          placeholder="Référence"
          value={v.ref || ""}
          onChange={(e) => setField("ref", e.target.value)}
          className="h-9 text-sm"
          data-testid={`${testid}-ref`}
        />
        {withKw && (
          <Input
            placeholder="kW (ex: 3.0)"
            value={v.kw || ""}
            onChange={(e) => setField("kw", e.target.value)}
            className="h-9 text-sm"
            data-testid={`${testid}-kw`}
          />
        )}
        <Input
          placeholder="Spec (ex: 3 HP)"
          value={v.spec || ""}
          onChange={(e) => setField("spec", e.target.value)}
          className={`h-9 text-sm ${withKw ? "" : "col-span-2"}`}
          data-testid={`${testid}-spec`}
        />
      </div>
    </div>
  );
}

function Field({ label, hint, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs font-semibold">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
